import { SubscriptionTier } from '../../subscription/api-types';
import { FeatureId } from '../access/feature-access/types';
import { AccessLevel } from '../access/types';
import { DirectFeatureAccessUserGroupId, SpecialUserGroupId } from '../user-groups/types';
import { CHAT_TOKEN_LIMITS, CONNECTOR_TOOL_CALL_LIMITS } from './plan-caps';
import { EntitlementKey } from './registry';
import {
  AgreementLimitValue,
  EntitlementJson,
  EntitlementScope,
  EntitlementScopeType,
  EntitlementSource,
  LiveAgreement,
} from './types';

/** One right in an organization's licence: what it is and until when, with no provenance. */
export interface LicenceRight {
  key: EntitlementKey;
  scope: EntitlementScope;
  level: AccessLevel | null;
  value: EntitlementJson | null;
  effectiveUntil: Date | null;
}

/**
 * What an organization on the record has from its agreement items, resolved when one of its members
 * was loaded and carried on that member's own groups. It lives for that load (a request, or a job
 * that loads the user): a check counts a right or an agreement only before its `effectiveUntil`, and
 * whatever starts or is revoked later counts from the next load. Keys, values and dates only, since
 * it is sent to the UI.
 */
export interface Licence {
  organizationId: number;
  resolvedAt: Date;
  horizon: Date | null;
  rights: readonly LicenceRight[];
  agreements: readonly LiveAgreement[];
}

// Set by the backend while licences are read from the record. The UI never sets it: its copy of the
// user is advisory, and the backend answers every request from its own load.
let licenceResolutionRequired = false;

export function setLicenceResolutionRequired(required: boolean): void {
  licenceResolutionRequired = required;
}

interface LicensedUser {
  id?: number | null;
  groups?: ReadonlyArray<{ id: number | null; licence?: Licence | null }> | null;
}

/**
 * While licences are read from the record, a user's own groups carry `licence` from the lineage
 * loaders: the organization's licence, or null for a group of an organization not on the record. A
 * user built some other way has none, and its checks would quietly give an organization on the
 * record its legacy answers, so this throws instead.
 */
export function assertLicenceResolved(user: LicensedUser): void {
  if (!licenceResolutionRequired) {
    return;
  }
  const unresolved = (user.groups ?? []).find(group => group.licence === undefined);
  if (unresolved) {
    throw Error(`User ${user.id} has group ${unresolved.id} without a licence: load it through the lineage loaders`);
  }
}

/** The product key each product group stands for. */
export const productKeyByGroupId: ReadonlyMap<number, EntitlementKey> = new Map([
  [SpecialUserGroupId.API, EntitlementKey.API],
  [SpecialUserGroupId.EU_PAI, EntitlementKey.EU_PAI],
  [SpecialUserGroupId.IMPACT_EXPORT, EntitlementKey.IMPACT_EXPORT],
  [SpecialUserGroupId.EDIT_ALL_WEIGHTSETS, EntitlementKey.EDIT_ALL_WEIGHT_SETS],
  [SpecialUserGroupId.GISE_STATEMENTS, EntitlementKey.GISE_STATEMENTS],
  [SpecialUserGroupId.CSRD_GROUPFOLIO_CONSTITUENT_EXPORT, EntitlementKey.CSRD_GROUPFOLIO_CONSTITUENT_EXPORT],
  [SpecialUserGroupId.SDG_ALIGNMENT, EntitlementKey.SDG_ALIGNMENT],
  [SpecialUserGroupId.EU_ELIGIBILITIES, EntitlementKey.EU_TAXONOMY],
  [SpecialUserGroupId.NET_IMPACT_USERS, EntitlementKey.NET_IMPACT],
]);

/** The package each coverage group carries: the group's own access rows are the package. */
export const coverageKeyByGroupId: ReadonlyMap<number, EntitlementKey> = new Map([
  [SpecialUserGroupId.STANDARD_COVERAGE, EntitlementKey.COVERAGE_STANDARD],
  [SpecialUserGroupId.EXTENDED_COVERAGE, EntitlementKey.COVERAGE_EXTENDED],
  [SpecialUserGroupId.CSRD_DATA_SET_EXTENDED, EntitlementKey.COVERAGE_CSRD_DATA_SET_EXTENDED],
  [SpecialUserGroupId.CSRD_DATA_SET_BENCHMARK, EntitlementKey.COVERAGE_CSRD_DATA_SET_BENCHMARK],
]);

/** Segment groups: who a customer is (client, investor, company platform), not what it bought. */
export const segmentKeyByGroupId: ReadonlyMap<number, EntitlementKey> = new Map([
  [SpecialUserGroupId.CLIENTS, EntitlementKey.SEGMENT_CLIENTS],
  [SpecialUserGroupId.CLIENTS_AND_FREE_TRIAL_USERS, EntitlementKey.SEGMENT_CLIENTS_AND_FREE_TRIAL_USERS],
  [SpecialUserGroupId.FREE_TRIAL, EntitlementKey.SEGMENT_FREE_TRIAL],
  [SpecialUserGroupId.INVESTOR_STANDARD_CLIENTS, EntitlementKey.SEGMENT_INVESTOR_STANDARD_CLIENTS],
  [SpecialUserGroupId.INVESTOR_FREE_CLIENTS, EntitlementKey.SEGMENT_INVESTOR_FREE_CLIENTS],
  [SpecialUserGroupId.COMPANY_PLATFORM_STANDARD_CLIENTS, EntitlementKey.SEGMENT_COMPANY_PLATFORM_STANDARD_CLIENTS],
  [SpecialUserGroupId.COMPANY_PLATFORM_FREE_CLIENTS, EntitlementKey.SEGMENT_COMPANY_PLATFORM_FREE_CLIENTS],
  [SpecialUserGroupId.COMPANY_PLATFORM_SELF_SIGNUP_USERS, EntitlementKey.SEGMENT_COMPANY_PLATFORM_SELF_SIGNUP_USERS],
]);

/** The product key a matrix feature becomes: the lowercased `FeatureId` wherever it is sold. */
export const productKeyByFeatureId: Partial<Record<FeatureId, EntitlementKey>> = {
  [FeatureId.API]: EntitlementKey.API,
  [FeatureId.EU_PAI]: EntitlementKey.EU_PAI,
  [FeatureId.IMPACT_EXPORT]: EntitlementKey.IMPACT_EXPORT,
  [FeatureId.EDIT_ALL_WEIGHT_SETS]: EntitlementKey.EDIT_ALL_WEIGHT_SETS,
  [FeatureId.GISE_STATEMENTS]: EntitlementKey.GISE_STATEMENTS,
  [FeatureId.SDG_ALIGNMENT]: EntitlementKey.SDG_ALIGNMENT,
  [FeatureId.EU_TAXONOMY]: EntitlementKey.EU_TAXONOMY,
  [FeatureId.NET_IMPACT]: EntitlementKey.NET_IMPACT,
  [FeatureId.SEARCHBAR]: EntitlementKey.SEARCHBAR,
  [FeatureId.PRODUCT_VIEW]: EntitlementKey.PRODUCT_VIEW,
  [FeatureId.PRODUCT_SEARCH]: EntitlementKey.PRODUCT_SEARCH,
  [FeatureId.BLANKET_LIMITED]: EntitlementKey.BLANKET_LIMITED,
  [FeatureId.DOWNLOAD_AS_EXCEL]: EntitlementKey.DOWNLOAD_AS_EXCEL,
  [FeatureId.EDIT_CURRENT_WEIGHT_SET]: EntitlementKey.EDIT_CURRENT_WEIGHT_SET,
  [FeatureId.TIME_TRACKING]: EntitlementKey.TIME_TRACKING,
  [FeatureId.DOWNLOAD_IMAGE]: EntitlementKey.DOWNLOAD_IMAGE,
};

// Groups that stand for something a licence holds. Reached from a group carrying a licence, they
// count only while the licence holds them, however they are reached; anything beyond them is still
// reached. ACCESS_TO_HOME_DOWNLOADS_AND_PRODUCTS has no item of its own: a licence never holds it.
export const licenceGroupIds: ReadonlySet<number> = new Set([
  ...productKeyByGroupId.keys(),
  ...coverageKeyByGroupId.keys(),
  SpecialUserGroupId.ACCESS_TO_HOME_DOWNLOADS_AND_PRODUCTS,
  SpecialUserGroupId.CLIENTS_AND_FREE_TRIAL_USERS,
  SpecialUserGroupId.FREE_TRIAL,
]);

/** Groups whose AVAILABLE matrix rows grant nothing through a group carrying a licence. */
export const matrixGroupIdsOffTheRecord: ReadonlySet<number> = new Set([
  ...segmentKeyByGroupId.keys(),
  ...Object.values(DirectFeatureAccessUserGroupId).filter((id): id is number => typeof id === 'number'),
]);

const organizationWide: EntitlementScope = { scopeType: null, scopeId: null, scopeCompanyId: null };

// A licence that reached the UI as JSON carries its dates as strings.
function isLiveAt(effectiveUntil: Date | string | null, now: number): boolean {
  return effectiveUntil === null || new Date(effectiveUntil).getTime() > now;
}

function isSameScope(a: EntitlementScope, b: EntitlementScope): boolean {
  return a.scopeType === b.scopeType && a.scopeId === b.scopeId && a.scopeCompanyId === b.scopeCompanyId;
}

/** The licence's rights of the key with exactly this scope that still count at `now`. */
function findLiveRights(licence: Pick<Licence, 'rights'>, key: EntitlementKey, scope: EntitlementScope, now: number) {
  return licence.rights.filter(
    right => right.key === key && isSameScope(right.scope, scope) && isLiveAt(right.effectiveUntil, now),
  );
}

export function holdsRight(licence: Licence, key: EntitlementKey, scope = organizationWide, now = Date.now()): boolean {
  return findLiveRights(licence, key, scope, now).length > 0;
}

export function hasLiveAgreement(licence: Licence, source: EntitlementSource, now = Date.now()): boolean {
  return licence.agreements.some(agreement => agreement.source === source && isLiveAt(agreement.effectiveUntil, now));
}

/** The licence groups a licence holds at `now`. */
function findHeldGroupIds(licence: Licence, now: number): number[] {
  const byKey = [...productKeyByGroupId, ...coverageKeyByGroupId].filter(([, key]) =>
    holdsRight(licence, key, undefined, now),
  );
  const contract = hasLiveAgreement(licence, EntitlementSource.CONTRACT, now);
  const trial = hasLiveAgreement(licence, EntitlementSource.TRIAL, now);
  return [
    ...byKey.map(([groupId]) => groupId),
    ...(contract || trial ? [SpecialUserGroupId.CLIENTS_AND_FREE_TRIAL_USERS] : []),
    ...(trial && !contract ? [SpecialUserGroupId.FREE_TRIAL] : []),
  ];
}

/**
 * The groups reached from a group carrying `licence`, as the record answers: the licence groups
 * among them dropped and those the licence holds added. The rule does not depend on how a group was
 * reached, so it applies to any set of ids reached from the organization's groups. An added group
 * brings only itself: STANDARD_COVERAGE's parent is CLIENTS, a segment, which stays structural.
 */
export function toLicensedGroupIds(groupIds: readonly number[], licence: Licence, now = Date.now()): number[] {
  const kept = groupIds.filter(id => !licenceGroupIds.has(id));
  const added = findHeldGroupIds(licence, now).filter(id => !kept.includes(id));
  return [...kept, ...added];
}

interface ClosureGroup {
  id: number | null;
  parents?: ClosureGroup[];
  licence?: Licence | null;
}

function collectAncestry(group: ClosureGroup, into: number[]): void {
  if (group.id !== null) into.push(group.id);
  group.parents?.forEach(parent => {
    collectAncestry(parent, into);
  });
}

export interface UserGroupClosure {
  // Reached from own groups of organizations not on the record, as before.
  legacyGroupIds: number[];
  // Reached from own groups carrying a licence, by the licence rule.
  licensedGroupIds: number[];
  // The licences of the user's organizations on the record.
  licences: Licence[];
}

/**
 * A user's group closure from its own groups, each in its organization's mode: a group carrying a
 * licence reaches what the licence rule gives, any other group what it reaches structurally. The
 * union is the closure, so one organization's missing right never removes another's grant.
 */
export function toUserGroupClosure(ownGroups: readonly ClosureGroup[], now = Date.now()): UserGroupClosure {
  const legacyGroupIds: number[] = [];
  const licensedGroupIds: number[] = [];
  const licences: Licence[] = [];
  for (const group of ownGroups) {
    const ancestry: number[] = [];
    collectAncestry(group, ancestry);
    if (group.licence) {
      licensedGroupIds.push(...toLicensedGroupIds(ancestry, group.licence, now));
      if (!licences.some(({ organizationId }) => organizationId === group.licence!.organizationId)) {
        licences.push(group.licence);
      }
    } else {
      legacyGroupIds.push(...ancestry);
    }
  }
  return { legacyGroupIds, licensedGroupIds, licences };
}

/**
 * Whether one of the user's organizations on the record holds the key now, with exactly this scope
 * (organisation-wide when none is given). Synchronous: it reads the licences the loaders put on the
 * user's own groups.
 */
export function hasLicensedRight(
  user: LicensedUser | undefined | null,
  key: EntitlementKey,
  scope?: EntitlementScope,
): boolean {
  if (!user) {
    return false;
  }
  assertLicenceResolved(user);
  const now = Date.now();
  return (user.groups ?? []).some(group => !!group.licence && holdsRight(group.licence, key, scope, now));
}

/** A quota's allowance from a licence: its limit and window, and until when it holds. */
export interface QuotaAllowance {
  limit: number;
  // Null: the quota's own period.
  windowDays: number | null;
  effectiveUntil: Date | null;
}

/** The scope of one client company of a consultant. */
export function toCompanyScope(companyId: string): EntitlementScope {
  return { scopeType: EntitlementScopeType.COMPANY, scopeId: companyId, scopeCompanyId: null };
}

type PaidDmaTier = Exclude<SubscriptionTier, 'starter'>;

/** The plan key of each paid DMA tier, highest first. Starter is the absence of a plan item. */
export const dmaPlanKeyByTier: ReadonlyArray<[PaidDmaTier, EntitlementKey]> = [
  ['enterprise', EntitlementKey.DMA_PLAN_ENTERPRISE],
  ['pro', EntitlementKey.DMA_PLAN_PRO],
];

export function isDmaPlanKey(key: string): boolean {
  return dmaPlanKeyByTier.some(([, planKey]) => (planKey as string) === key);
}

function toEffectiveUntil(effectiveUntil: Date | string | null): Date | null {
  return effectiveUntil === null ? null : new Date(effectiveUntil);
}

/** The licence's live plan items of exactly this scope, each as its tier. */
function findLivePlans(licence: Pick<Licence, 'rights'>, scope: EntitlementScope, now: number) {
  return dmaPlanKeyByTier.flatMap(([tier, key]) =>
    findLiveRights(licence, key, scope, now).map(({ effectiveUntil }) => ({
      tier,
      effectiveUntil: toEffectiveUntil(effectiveUntil),
    })),
  );
}

/**
 * The DMA plan a licence holds now for exactly this scope (organisation-wide when none is given):
 * the highest tier among its live plan items, Starter without one. A client company's plan comes
 * only from items of that company, and the organisation's only from organisation-wide items.
 */
export function dmaPlanOf(
  licence: Pick<Licence, 'rights'>,
  scope = organizationWide,
  now = Date.now(),
): SubscriptionTier {
  return findLivePlans(licence, scope, now)[0]?.tier ?? 'starter';
}

// The quotas whose allowance a plan sets, by tier.
const planCapsByKey: Partial<Record<EntitlementKey, Record<PaidDmaTier, number>>> = {
  [EntitlementKey.CHAT_TOKENS]: CHAT_TOKEN_LIMITS,
  [EntitlementKey.MCP_CONNECTOR_TOOL_CALLS]: CONNECTOR_TOOL_CALL_LIMITS,
};

export function isPlanCappedKey(key: string): boolean {
  return planCapsByKey[key as EntitlementKey] !== undefined;
}

function isMoreGenerous(candidate: QuotaAllowance, best: QuotaAllowance): boolean {
  if (candidate.limit !== best.limit) {
    return candidate.limit > best.limit;
  }
  return (
    best.effectiveUntil !== null &&
    (candidate.effectiveUntil === null || candidate.effectiveUntil.getTime() > best.effectiveUntil.getTime())
  );
}

/**
 * The allowance of a quota a licence holds now for exactly this scope (organisation-wide when none
 * is given): the greatest among its live limit items and, for the quotas a plan caps, its live plan
 * items at their tier's cap in the quota's own period. A tie goes to the later end. Limits of one key
 * and scope share a window, and the commands refuse another window beside a plan, so the greatest
 * limit is also the most generous allowance. Null when the licence holds none.
 */
export function quotaAllowanceOf(
  licence: Pick<Licence, 'rights'>,
  key: EntitlementKey,
  scope = organizationWide,
): QuotaAllowance | null {
  const now = Date.now();
  const limits = findLiveRights(licence, key, scope, now).flatMap(({ value, effectiveUntil }) => {
    const { limit, windowDays } = (value ?? {}) as Partial<AgreementLimitValue>;
    return typeof limit === 'number'
      ? [{ limit, windowDays: windowDays ?? null, effectiveUntil: toEffectiveUntil(effectiveUntil) }]
      : [];
  });
  const caps = planCapsByKey[key];
  const plans = caps
    ? findLivePlans(licence, scope, now).map(({ tier, effectiveUntil }) => ({
        limit: caps[tier],
        windowDays: null,
        effectiveUntil,
      }))
    : [];
  return [...limits, ...plans].reduce<QuotaAllowance | null>(
    (best, next) => (best && !isMoreGenerous(next, best) ? best : next),
    null,
  );
}
